import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Trophy, Award, Crown, ArrowUpRight, Check, Banknote, Zap, Shield,
  Globe2, ChevronRight, Calendar, Wallet, TrendingUp, Users,
} from "lucide-react";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const API = `${BACKEND_URL}/api`;

const TIERS = [
  {
    id: "silver",
    name: "Silver",
    Icon: Award,
    threshold: "0 – £5k MRR referred",
    commission: "20%",
    description: "Starting tier. Everyone begins here.",
    perks: [
      "20% recurring commission on every referred account",
      "Monthly Bank Transfer (UK BACS) — 2 working days",
      "Public Connexis Partner badge",
      "Standard creatives library access",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    Icon: Trophy,
    threshold: "£5k – £25k MRR referred",
    commission: "30%",
    description: "Most active partners sit here.",
    perks: [
      "30% recurring commission — increase backdated to month start",
      "Weekly Stripe Connect Express payout (instant)",
      "Co-branded case-study production (we write it)",
      "Direct Slack channel with Partner Success",
      "Early access to new sectors before public launch",
    ],
    featured: true,
  },
  {
    id: "platinum",
    name: "Platinum",
    Icon: Crown,
    threshold: "£25k+ MRR referred",
    commission: "40% + bonuses",
    description: "Top 1% — partner programme reserved for inner circle.",
    perks: [
      "40% recurring commission for the lifetime of every account",
      "Quarterly performance bonus pool (split across the cohort)",
      "Listed publicly on the Partner Leaderboard with verified badge",
      "Joint go-to-market — Connexis funds £1k/month of paid media for your funnel",
      "Annual partner retreat (flights + hotel paid)",
      "First refusal on exclusive sector rights in your region",
    ],
  },
];

const FAQ = [
  {
    q: "When and how do I get paid?",
    a: "Silver: monthly BACS the 1st of each month. Growth & Platinum: weekly Stripe Connect Express — instant to your bank account once approved. Connect onboarding is a 5-minute Stripe-hosted flow (KYC + bank details).",
  },
  {
    q: "How is commission calculated?",
    a: "Recurring percentage of the platform fee Connexis charges your referred account, for the full lifetime of that account. Top-ups and one-off invoices are included. Refunds reverse pro-rata in the same cycle.",
  },
  {
    q: "What counts as a successful referral?",
    a: "Any prospect who lands on Connexis via your tracking link (cookie window 90 days), books a demo, and signs a paid contract. Self-serve top-ups under £5,000 don't qualify as a referral — only paid plan accounts.",
  },
  {
    q: "Can I refer my own portfolio companies?",
    a: "Yes — if you're a fund or accelerator, you can refer any portfolio company you don't already operate. We have a separate Fund Partner tier for VCs referring 5+ companies — talk to us.",
  },
  {
    q: "Is there a minimum payout amount?",
    a: "£50 for BACS, £25 for Stripe Connect Express. Below that, your balance rolls into the next cycle.",
  },
  {
    q: "Can I be removed from the programme?",
    a: "Only for explicit ToS breaches (incentive fraud, spam outreach, undisclosed paid placement). We don't deactivate for low performance — partners who simply aren't active stay Silver indefinitely.",
  },
];

function Stat({ label, value, sub }) {
  return (
    <div className="border-l-2 border-[#0055FF]/30 pl-4">
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Partners() {
  const rain = useCodeRain("partners", "dark");
  const [stats, setStats] = useState({ total_partners: null, top_commission: null });

  useEffect(() => {
    axios.get(`${API}/public/affiliate-leaderboard?limit=1`)
      .then((r) => setStats({
        total_partners: r.data.total_partners,
        top_commission: r.data.partners?.[0]?.commission,
      }))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="partners-page">
      {/* ====================== HERO ====================== */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_-10%,rgba(0,85,255,0.10),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_110%,rgba(34,211,166,0.10),transparent_50%)] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-28 relative">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#0055FF] font-semibold mb-5">
                <Trophy className="size-3.5" /> Connexis Partner Programme
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-semibold text-slate-900 leading-[1.05]">
                Earn recurring commission for the<br />
                <span className="bg-gradient-to-r from-[#0055FF] to-[#22D3A6] bg-clip-text text-transparent">lifetime of every account</span>
                <span className="text-slate-900"> you refer.</span>
              </h1>
              <p className="mt-6 text-slate-600 max-w-xl text-[16px] leading-relaxed">
                Up to 40% of platform fee · paid weekly via Stripe · transparent leaderboard. We pay partners more than any other infrastructure platform in financial services.
              </p>
              <div className="mt-9 flex items-center gap-3 flex-wrap">
                <Link to="/partners/apply">
                  <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 px-6" data-testid="partners-cta-apply">
                    Apply to join <ArrowUpRight className="size-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/leaderboard">
                  <Button variant="outline" className="rounded-full h-11 px-6" data-testid="partners-cta-leaderboard">
                    View leaderboard <ChevronRight className="size-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5 lg:gap-x-7">
              <Stat label="Active partners" value={stats.total_partners ?? "—"} sub="globally" />
              <Stat label="Top earner · 30d" value={stats.top_commission ? `£${Math.round(stats.top_commission).toLocaleString()}` : "—"} sub="commission paid" />
              <Stat label="Payout cadence" value="Weekly" sub="Growth & Platinum tiers" />
              <Stat label="Cookie window" value="90 days" sub="first-touch attribution" />
            </div>
          </div>
        </div>
      </section>

      {/* ====================== HOW IT WORKS ====================== */}
      <section className="border-b border-slate-200 bg-slate-50/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          {/* Cross-program callout: this page is for REFERRERS, lead producers go elsewhere */}
          <div className="mb-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="partners-publisher-callout">
            <div className="flex items-start gap-3">
              <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white shrink-0"><TrendingUp className="size-5" strokeWidth={1.5} /></span>
              <div>
                <div className="font-semibold text-slate-900">Producing leads, not referrals?</div>
                <div className="text-[13.5px] text-slate-600 mt-0.5">If you generate UK finance leads (paid search, SEO, forms) and want to <b>sell them to our buyers</b>, you want our Publisher Program — 90% revenue share per accepted lead.</div>
              </div>
            </div>
            <Link to="/publisher/apply" className="shrink-0">
              <Button variant="outline" className="rounded-full" data-testid="partners-to-publisher">Apply to sell leads →</Button>
            </Link>
          </div>

          <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold mb-3">How the programme works</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl">Four steps from sign-up to first payout.</h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: "01", Icon: Users, title: "Apply in 60 seconds", body: "Submit your background and audience. We review every application personally — most are approved same day." },
              { n: "02", Icon: Globe2, title: "Get your tracking link", body: "Unique UTM-tagged URL with 90-day cookie attribution. Drop it into your newsletter, LinkedIn, or content." },
              { n: "03", Icon: TrendingUp, title: "Earn 20–40% recurring", body: "Tier-based commission on the platform fee every account pays — for as long as they stay on Connexis." },
              { n: "04", Icon: Wallet, title: "Get paid weekly", body: "Growth & Platinum: instant Stripe payouts every Friday. Silver: monthly BACS on the 1st." },
            ].map((s) => (
              <div key={s.n} className="relative p-7 bg-white border border-slate-200 rounded-xl shadow-soft hover:border-[#0055FF]/30 transition-colors duration-300 group">
                <div className="flex items-start justify-between mb-5">
                  <s.Icon className="size-7 text-[#0055FF]" strokeWidth={1.4} />
                  <span className="font-mono text-[10.5px] tracking-[0.18em] text-slate-400">{s.n}</span>
                </div>
                <div className="font-medium text-[17px] text-slate-900 tracking-tight">{s.title}</div>
                <div className="text-[13.5px] text-slate-600 leading-relaxed mt-2.5">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== COMMISSION TIERS ====================== */}
      <section id="commission" className="scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold mb-3">Commission structure</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl">Three tiers. Real percentages. No fine-print.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl text-[15px]">Tier upgrades happen automatically based on referred MRR — there's no quarterly review, no sales calls. Hit the threshold and the new rate is backdated to the start of that calendar month.</p>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <Card
                key={t.id}
                className={`rounded-md overflow-hidden relative transition-all duration-300 ${
                  t.featured
                    ? "bg-slate-950 text-white border border-slate-800 shadow-[0_8px_40px_-12px_rgba(0,0,0,0.35)]"
                    : "bg-white border border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`tier-${t.id}`}
              >
                {/* Featured ribbon — refined, subtle, not a gradient strip */}
                {t.featured && (
                  <div className="absolute top-0 right-0 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-white/70 border-l border-b border-slate-800 bg-slate-900/60 backdrop-blur-sm">
                    Most active
                  </div>
                )}
                {/* Vertical accent rail — left edge only, subtle */}
                <div className={`absolute left-0 top-0 bottom-0 w-px ${t.featured ? "bg-white/15" : "bg-slate-200"}`} />

                <CardContent className="p-7">
                  <div className={`inline-flex items-center justify-center size-10 rounded-sm ${
                    t.featured ? "bg-white/8 text-white" : "bg-slate-100 text-slate-700"
                  }`}>
                    <t.Icon className="size-5" strokeWidth={1.5} />
                  </div>

                  <div className={`mt-5 font-semibold text-2xl tracking-tight ${t.featured ? "text-white" : "text-slate-900"}`}>
                    {t.name}
                  </div>
                  <div className={`text-[12px] mt-0.5 font-mono uppercase tracking-wide ${t.featured ? "text-white/45" : "text-slate-500"}`}>
                    {t.threshold}
                  </div>

                  <div className={`mt-7 pt-5 border-t ${t.featured ? "border-white/10" : "border-slate-100"}`}>
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-5xl font-semibold tracking-tight tabular-nums ${t.featured ? "text-white" : "text-slate-900"}`}>
                        {t.commission}
                      </span>
                      <span className={`text-sm ${t.featured ? "text-white/55" : "text-slate-500"}`}>recurring</span>
                    </div>
                    <div className={`text-[12px] mt-1 ${t.featured ? "text-white/55" : "text-slate-500"}`}>
                      {t.description}
                    </div>
                  </div>

                  <ul className={`mt-6 space-y-3 pt-5 border-t ${t.featured ? "border-white/10" : "border-slate-100"}`}>
                    {t.perks.map((perk, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-[13px] leading-relaxed ${t.featured ? "text-white/80" : "text-slate-700"}`}>
                        <Check className={`size-3.5 shrink-0 mt-0.5 ${t.featured ? "text-white" : "text-slate-900"}`} strokeWidth={2.5} />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== WORKED EXAMPLE ====================== */}
      <section className="border-y border-slate-200 bg-gradient-to-b from-slate-900 to-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(0,85,255,0.15),transparent_60%)] pointer-events-none" />
        {rain.on && <CodeRain variant="dark" intensity={rain.intensity} />}
        <div className="max-w-5xl mx-auto px-6 py-20 relative">
          <div className="text-[11px] uppercase tracking-widest text-[#22D3A6] font-semibold mb-3 font-mono">Worked example</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold max-w-2xl">A Growth partner refers one £1,499/month account.</h2>
          <p className="mt-3 text-[14px] text-white/55 max-w-2xl">Typical Growth-tier buyer: £149 platform plan + ~£1,350 in wallet top-ups & lead packs per month. You commission on the platform fee portion only.</p>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-xl overflow-hidden">
            <div className="bg-slate-900 p-6">
              <div className="text-[10.5px] uppercase tracking-widest text-white/40">Account spends</div>
              <div className="text-3xl font-semibold tabular-nums mt-2">£1,499</div>
              <div className="text-[12px] text-white/50 mt-1 font-mono">/ month total (plan + leads)</div>
            </div>
            <div className="bg-slate-900 p-6">
              <div className="text-[10.5px] uppercase tracking-widest text-white/40">Platform fee (4%)</div>
              <div className="text-3xl font-semibold tabular-nums mt-2">£59.96</div>
              <div className="text-[12px] text-white/50 mt-1 font-mono">commissionable base</div>
            </div>
            <div className="bg-slate-900 p-6">
              <div className="text-[10.5px] uppercase tracking-widest text-white/40">Your share (30%)</div>
              <div className="text-3xl font-semibold tabular-nums mt-2 text-[#22D3A6]">£17.99</div>
              <div className="text-[12px] text-white/50 mt-1 font-mono">per month, recurring</div>
            </div>
            <div className="bg-slate-900 p-6">
              <div className="text-[10.5px] uppercase tracking-widest text-white/40">12-month LTV</div>
              <div className="text-3xl font-semibold tabular-nums mt-2 text-[#22D3A6]">£215.86</div>
              <div className="text-[12px] text-white/50 mt-1 font-mono">from one referral</div>
            </div>
          </div>

          <p className="mt-8 text-[14px] text-white/60 max-w-2xl leading-relaxed">
            Refer 10 accounts as a Growth partner → £2,158 recurring annually, paid weekly. Cross to Platinum (40%) and the same 10 accounts become £2,878. Most of our top earners reach Growth within 90 days.
          </p>
        </div>
      </section>

      {/* ====================== WHY US ====================== */}
      <section>
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold mb-3">Why Connexis pays more</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl">Built for partners who actually care about retention.</h2>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { Icon: Banknote, title: "Lifetime commission", body: "We don't 12-month-cliff like most SaaS. Refer once, earn for as long as that account stays paying." },
              { Icon: Zap, title: "Stripe Connect Express", body: "Growth & Platinum payouts hit your bank account same-day via Stripe Connect — not in 30/60-day net cycles." },
              { Icon: Shield, title: "First-touch 90-day cookie", body: "Most platforms use last-touch with a 30-day window. We honour first-touch for 90 days — your work counts even if someone else closes them." },
              { Icon: Trophy, title: "Public leaderboard", body: "Top earners get verified-partner badges and a permanent profile on our /leaderboard page — anonymised but distinguishable." },
              { Icon: Calendar, title: "No clawbacks after 90d", body: "If a referred account churns 90+ days in, your commission to date is locked. We carry the refund risk, not you." },
              { Icon: Users, title: "Real Partner Success", body: "Dedicated Slack channel from Growth tier. Joint case studies. Co-funded paid media for Platinum partners — we treat partners like a sales team." },
            ].map((b, i) => (
              <div key={i} className="group relative p-7 border-t-2 border-slate-100 hover:border-[#0055FF] transition-colors duration-300">
                {/* premium icon — no rounded square; bare icon + brand accent hairline */}
                <b.Icon className="size-7 text-[#0055FF] mb-6" strokeWidth={1.4} />
                <div className="font-medium text-[17px] text-slate-900 tracking-tight">{b.title}</div>
                <div className="text-[13.5px] text-slate-600 leading-relaxed mt-2.5">{b.body}</div>
                {/* subtle hover indicator */}
                <div className="absolute top-0 left-0 h-0.5 w-0 bg-gradient-to-r from-[#0055FF] to-[#22D3A6] group-hover:w-12 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== FAQ ====================== */}
      <section className="border-t border-slate-200 bg-slate-50/40">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="text-[11px] uppercase tracking-widest text-[#0055FF] font-semibold mb-3">Frequently asked</div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900">Questions partners actually ask.</h2>
          <div className="mt-10 divide-y divide-slate-200">
            {FAQ.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between text-[15px] font-medium text-slate-900 hover:text-[#0055FF] transition-colors">
                  <span>{f.q}</span>
                  <ChevronRight className="size-4 text-slate-400 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ====================== CTA ====================== */}
      <section className="border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900 max-w-2xl mx-auto">Ready to start earning?</h2>
          <p className="mt-4 text-slate-600 max-w-xl mx-auto text-[15px]">Submit a short application — we review every one personally and most partners are approved the same day.</p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/partners/apply">
              <Button className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white h-11 px-7" data-testid="partners-cta-bottom-apply">
                Apply to the programme <ArrowUpRight className="size-4 ml-1" />
              </Button>
            </Link>
            <Link to="/leaderboard">
              <Button variant="outline" className="rounded-full h-11 px-6" data-testid="partners-cta-bottom-leaderboard">
                See current leaderboard
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
